import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Lock, ArrowLeft, RefreshCw, DollarSign, Eye, Users, MousePointerClick,
  ChevronDown, ChevronRight, Megaphone, TrendingUp, Search, Video, Image as ImageIcon
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid
} from 'recharts';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

// Accent colors: video = bird identities; static = distinct banner palette.
const VIDEO_COLORS = { 1: '#FCD34D', 2: '#10B981', 3: '#EC4899', 4: '#3B82F6', 5: '#8B5CF6', 6: '#A16207' };
const STATIC_COLORS = ['#7c3aed', '#f59e0b', '#06b6d4', '#64748b', '#ec4899', '#10b981', '#f97316', '#3b82f6', '#22c55e', '#d946ef'];
const adColor = (type, id) => type === 'static'
  ? (STATIC_COLORS[(id - 1) % STATIC_COLORS.length] || '#7c3aed')
  : (VIDEO_COLORS[id] || '#64748b');

const key = (a) => `${a.ad_type}:${a.ad_id}`;
const fmt = (n) => (n ?? 0).toLocaleString();
const money = (n) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const isoDaysAgo = (d) => { const dt = new Date(); dt.setDate(dt.getDate() - d); return dt.toISOString().slice(0, 10); };
const prettyPlacement = (p) => ({ 'game-interstitial': 'In-game', 'homepage': 'Homepage', 'leaderboard': 'Leaderboard' }[p] || p);

const TypePill = ({ type }) => (
  <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wide ${
    type === 'video' ? 'bg-blue-500/15 text-blue-300' : 'bg-violet-500/15 text-violet-300'
  }`}>
    {type === 'video' ? <Video className="w-3 h-3" /> : <ImageIcon className="w-3 h-3" />}
    {type}
  </span>
);

const AdBadge = ({ ad }) => (
  <div className="min-w-0">
    <span className="flex items-center gap-2 min-w-0">
      <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: adColor(ad.ad_type, ad.ad_id) }} />
      <span className="text-xs font-mono text-muted-foreground shrink-0">#{ad.ad_id}</span>
      <span className="font-medium truncate">{ad.name}</span>
      <TypePill type={ad.ad_type} />
    </span>
    {ad.placements?.length > 0 && (
      <span className="block text-[11px] text-muted-foreground mt-0.5 truncate">
        {ad.placements.map(prettyPlacement).join(' · ')}
      </span>
    )}
  </div>
);

const PasswordGate = ({ onUnlock }) => {
  const [pw, setPw] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await fetch(`${API}/api/ads/analytics?password=${encodeURIComponent(pw)}`);
      if (res.ok) { sessionStorage.setItem('rta_pw', pw); onUnlock(pw); }
      else setError('Incorrect password.');
    } catch { setError('Could not reach the server.'); }
    finally { setLoading(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-sm">
        <Card className="p-8 bg-gradient-card border-border text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-primary/15 flex items-center justify-center">
            <Lock className="w-6 h-6 text-primary" />
          </div>
          <h1 className="text-xl font-heading font-bold mb-1">Ad Analytics</h1>
          <p className="text-sm text-muted-foreground mb-6">Admin access only</p>
          <form onSubmit={submit} className="space-y-3">
            <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)}
              placeholder="Enter admin password" data-testid="ads-password-input" autoFocus />
            {error && <p className="text-sm text-destructive" data-testid="ads-error">{error}</p>}
            <Button type="submit" disabled={loading || !pw} className="w-full bg-gradient-primary text-primary-foreground font-semibold" data-testid="ads-unlock-btn">
              {loading ? 'Checking…' : 'Unlock Dashboard'}
            </Button>
          </form>
        </Card>
      </motion.div>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value, accent = 'text-primary', sub }) => (
  <Card className="p-4 bg-card/60 border-border">
    <div className="flex items-center gap-2 mb-1.5">
      <Icon className={`w-4 h-4 ${accent}`} />
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
    </div>
    <div className="text-2xl font-heading font-bold tabular-nums">{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
  </Card>
);

const AdsAnalyticsPage = () => {
  const navigate = useNavigate();
  const [password] = useState(sessionStorage.getItem('rta_pw') || '');
  const [unlocked, setUnlocked] = useState(!!sessionStorage.getItem('rta_pw'));
  const [pw, setPw] = useState(sessionStorage.getItem('rta_pw') || '');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [start, setStart] = useState(isoDaysAgo(13));
  const [end, setEnd] = useState(isoDaysAgo(0));
  const [typeFilter, setTypeFilter] = useState('');   // '' | 'video' | 'static'
  const [openDay, setOpenDay] = useState(null);
  const [editRate, setEditRate] = useState({});

  const activePw = pw || password;

  const load = useCallback(async () => {
    if (!activePw) return;
    setLoading(true);
    try {
      const typeParam = typeFilter ? `&ad_type=${typeFilter}` : '';
      const res = await fetch(`${API}/api/ads/analytics?password=${encodeURIComponent(activePw)}&start=${start}&end=${end}${typeParam}`);
      if (res.status === 401) { sessionStorage.removeItem('rta_pw'); setUnlocked(false); return; }
      const d = await res.json();
      if (d.success) {
        setData(d);
        const rateMap = {};
        d.per_ad.forEach((a) => { rateMap[key(a)] = a.rate; });
        setEditRate(rateMap);
      }
    } catch { /* keep last */ } finally { setLoading(false); }
  }, [activePw, start, end, typeFilter]);

  useEffect(() => { if (unlocked) load(); }, [unlocked, load]);

  const saveRate = async (ad) => {
    const val = parseFloat(editRate[key(ad)]);
    if (Number.isNaN(val) || val === ad.rate) return;
    try {
      await fetch(`${API}/api/ads/config?password=${encodeURIComponent(activePw)}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ad_id: ad.ad_id, ad_type: ad.ad_type, rate: val }),
      });
      load();
    } catch { /* ignore */ }
  };

  const applyPreset = (days) => { setStart(isoDaysAgo(days)); setEnd(isoDaysAgo(0)); };

  const perAd = data?.per_ad || [];
  const daily = data?.daily || [];

  // Daily video/static split for the trend chart (oldest -> newest).
  const chartData = useMemo(() => {
    return [...daily].reverse().map((day) => {
      let vServe = 0, sServe = 0, vVal = 0, sVal = 0;
      (day.ads || []).forEach((a) => {
        if (a.ad_type === 'static') { sServe += a.impressions; sVal += a.est_value; }
        else { vServe += a.impressions; vVal += a.est_value; }
      });
      return {
        date: day.date.slice(5),  // MM-DD
        videoServes: vServe, staticServes: sServe,
        videoValue: Math.round(vVal * 100) / 100, staticValue: Math.round(sVal * 100) / 100,
      };
    });
  }, [daily]);

  if (!unlocked) return <PasswordGate onUnlock={(p) => { setPw(p); setUnlocked(true); }} />;

  const totals = data?.totals || {};
  const maxValue = Math.max(1, ...perAd.map((a) => a.est_value));

  return (
    <div className="min-h-screen bg-background" data-testid="ads-dashboard">
      <div className="container mx-auto max-w-6xl px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate('/rta')} className="text-muted-foreground" data-testid="ads-back-rta">
              <ArrowLeft className="w-4 h-4 mr-1" /> RTA
            </Button>
            <h1 className="text-2xl font-heading font-bold flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-primary" /> Ad Analytics
            </h1>
          </div>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="ads-refresh">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {/* Date range + type filter */}
        <Card className="p-4 mb-6 bg-gradient-card border-border">
          <div className="flex items-end gap-3 flex-wrap">
            <div>
              <label className="text-xs text-muted-foreground block mb-1">From</label>
              <Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="w-[160px]" data-testid="ads-start-date" />
            </div>
            <div>
              <label className="text-xs text-muted-foreground block mb-1">To</label>
              <Input type="date" value={end} min={start} max={isoDaysAgo(0)} onChange={(e) => setEnd(e.target.value)} className="w-[160px]" data-testid="ads-end-date" />
            </div>
            <Button onClick={load} className="bg-gradient-primary text-primary-foreground font-semibold" data-testid="ads-search-btn">
              <Search className="w-4 h-4 mr-1.5" /> Search
            </Button>
            <div className="flex items-center gap-2 ml-auto">
              {[['Today', 0], ['7d', 6], ['14d', 13], ['30d', 29], ['90d', 89]].map(([label, d]) => (
                <button key={label} onClick={() => applyPreset(d)}
                  className="px-3 py-1.5 rounded-full text-xs font-semibold bg-muted/50 hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  data-testid={`ads-preset-${label.toLowerCase()}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3 pt-3 border-t border-border/60">
            <span className="text-xs text-muted-foreground mr-1">Type:</span>
            {[['All', ''], ['Video', 'video'], ['Static', 'static']].map(([label, val]) => (
              <button key={label} onClick={() => setTypeFilter(val)}
                className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                  typeFilter === val ? 'bg-primary text-primary-foreground' : 'bg-muted/50 hover:bg-muted text-muted-foreground'
                }`}
                data-testid={`ads-type-${label.toLowerCase()}`}>
                {label}
              </button>
            ))}
          </div>
        </Card>

        {/* Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Metric icon={Eye} label="Total Serves" value={fmt(totals.impressions)} accent="text-cyan-400" />
          <Metric icon={Users} label="Unique Visitors" value={fmt(totals.unique_visitors)} accent="text-violet-400" />
          <Metric icon={MousePointerClick} label="Total Clicks" value={fmt(totals.clicks)} accent="text-amber-400" />
          <Metric icon={DollarSign} label="Est. Value" value={money(totals.est_value)} accent="text-green-400"
            sub={`Video ${money(totals.video_value)} · Static ${money(totals.static_value)}`} />
        </div>

        {/* Estimated monthly revenue */}
        <Card className="p-5 mb-6 bg-gradient-primary/10 border border-green-500/30 flex items-center gap-4 flex-wrap" data-testid="ads-monthly-revenue">
          <div className="w-12 h-12 rounded-full bg-green-500/15 flex items-center justify-center shrink-0">
            <DollarSign className="w-6 h-6 text-green-400" />
          </div>
          <div className="min-w-0">
            <div className="text-xs text-muted-foreground uppercase tracking-wide">Estimated Monthly Revenue</div>
            <div className="text-3xl font-heading font-bold text-green-400 tabular-nums">{money(totals.est_monthly_revenue)}</div>
          </div>
          <div className="text-xs text-muted-foreground ml-auto text-right">
            Based on {money(totals.avg_daily_value)}/day avg<br />
            across {fmt(totals.active_days)} active day{totals.active_days === 1 ? '' : 's'} × 30
          </div>
        </Card>

        {/* Trend chart: daily serves (stacked) + est. value (lines), video vs static */}
        <Card className="p-5 bg-gradient-card border-border mb-6">
          <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" /> Daily Trend — Serves &amp; Value
          </h3>
          {chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground py-12 text-center">No activity to chart in this range yet.</p>
          ) : (
            <div className="h-72" data-testid="ads-trend-chart">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="date" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} />
                  <YAxis yAxisId="left" stroke="rgba(255,255,255,0.4)" fontSize={10} tickLine={false} allowDecimals={false} />
                  <YAxis yAxisId="right" orientation="right" stroke="rgba(34,197,94,0.6)" fontSize={10} tickLine={false}
                    tickFormatter={(v) => `$${v}`} />
                  <Tooltip
                    contentStyle={{ background: '#0b0f17', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontSize: 12 }}
                    formatter={(value, name) => (name.includes('Value') ? [`$${value}`, name] : [value, name])}
                  />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar yAxisId="left" dataKey="videoServes" name="Video serves" stackId="s" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                  <Bar yAxisId="left" dataKey="staticServes" name="Static serves" stackId="s" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="videoValue" name="Video Value" stroke="#22d3ee" strokeWidth={2} dot={{ r: 2 }} />
                  <Line yAxisId="right" type="monotone" dataKey="staticValue" name="Static Value" stroke="#22c55e" strokeWidth={2} dot={{ r: 2 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Per-ad breakdown */}
        <Card className="p-5 bg-gradient-card border-border mb-6">
          <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" /> Ads by Value ({data?.range?.start} → {data?.range?.end})
          </h3>
          {perAd.length === 0 ? (
            <p className="text-sm text-muted-foreground py-10 text-center" data-testid="ads-empty">
              No ad data in this range yet. Once players see ads (in-game video + homepage/leaderboard banners), serves and clicks will appear here.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ads-per-ad-table">
                <thead>
                  <tr className="text-xs text-muted-foreground uppercase tracking-wide border-b border-border">
                    <th className="text-left py-2 pr-3 font-semibold">Ad</th>
                    <th className="text-right py-2 px-3 font-semibold">Serves</th>
                    <th className="text-right py-2 px-3 font-semibold">Unique</th>
                    <th className="text-right py-2 px-3 font-semibold">Clicks</th>
                    <th className="text-right py-2 px-3 font-semibold">CTR</th>
                    <th className="text-right py-2 px-3 font-semibold">$/Serve</th>
                    <th className="text-right py-2 pl-3 font-semibold">Est.&nbsp;Value</th>
                  </tr>
                </thead>
                <tbody>
                  {perAd.map((a) => (
                    <tr key={key(a)} className="border-b border-border/40 hover:bg-muted/20" data-testid={`ads-row-${a.ad_type}-${a.ad_id}`}>
                      <td className="py-2.5 pr-3 max-w-[260px]">
                        <AdBadge ad={a} />
                        <div className="mt-1.5 h-1.5 rounded-full bg-muted/40 overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${(a.est_value / maxValue) * 100}%`, backgroundColor: adColor(a.ad_type, a.ad_id) }} />
                        </div>
                      </td>
                      <td className="text-right px-3 tabular-nums">{fmt(a.impressions)}</td>
                      <td className="text-right px-3 tabular-nums text-violet-300">{fmt(a.unique_visitors)}</td>
                      <td className="text-right px-3 tabular-nums text-amber-300">{fmt(a.clicks)}</td>
                      <td className="text-right px-3 tabular-nums text-muted-foreground">{a.ctr}%</td>
                      <td className="text-right px-3">
                        <input type="number" step="0.01" min="0"
                          value={editRate[key(a)] ?? a.rate}
                          onChange={(e) => setEditRate({ ...editRate, [key(a)]: e.target.value })}
                          onBlur={() => saveRate(a)}
                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                          className="w-20 text-right bg-muted/40 border border-border rounded-md px-2 py-1 tabular-nums focus:outline-none focus:ring-1 focus:ring-primary"
                          data-testid={`ads-rate-${a.ad_type}-${a.ad_id}`} />
                      </td>
                      <td className="text-right pl-3 tabular-nums font-semibold text-green-400">{money(a.est_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="text-xs text-muted-foreground mt-3">
                Est. Value = Serves × $/Serve (default $0.09). Edit any rate inline to recalculate. Ranked by value.
              </p>
            </div>
          )}
        </Card>

        {/* Daily breakdown */}
        <Card className="p-5 bg-gradient-card border-border">
          <h3 className="font-heading font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-cyan-400" /> Daily Breakdown
          </h3>
          {daily.length === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center">No days with ad activity in this range.</p>
          ) : (
            <div className="space-y-2" data-testid="ads-daily-list">
              {daily.map((day) => {
                const open = openDay === day.date;
                return (
                  <div key={day.date} className="rounded-xl border border-border overflow-hidden">
                    <button onClick={() => setOpenDay(open ? null : day.date)}
                      className="w-full flex items-center gap-3 px-4 py-3 bg-muted/20 hover:bg-muted/40 transition-colors text-left"
                      data-testid={`ads-day-${day.date}`}>
                      {open ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                      <span className="font-semibold tabular-nums">{day.date}</span>
                      <span className="flex items-center gap-4 ml-auto text-xs sm:text-sm">
                        <span className="text-cyan-300 tabular-nums">{fmt(day.impressions)} serves</span>
                        <span className="text-amber-300 tabular-nums hidden sm:inline">{fmt(day.clicks)} clicks</span>
                        <span className="text-green-400 font-semibold tabular-nums">{money(day.est_value)}</span>
                      </span>
                    </button>
                    {open && (
                      <div className="px-4 py-3 bg-card/40 overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-xs text-muted-foreground uppercase tracking-wide">
                              <th className="text-left py-1 pr-3 font-semibold">Ad</th>
                              <th className="text-right py-1 px-3 font-semibold">Serves</th>
                              <th className="text-right py-1 px-3 font-semibold">Unique</th>
                              <th className="text-right py-1 px-3 font-semibold">Clicks</th>
                              <th className="text-right py-1 pl-3 font-semibold">Value</th>
                            </tr>
                          </thead>
                          <tbody>
                            {day.ads.map((a) => (
                              <tr key={key(a)} className="border-t border-border/30">
                                <td className="py-1.5 pr-3 max-w-[240px]"><AdBadge ad={a} /></td>
                                <td className="text-right px-3 tabular-nums">{fmt(a.impressions)}</td>
                                <td className="text-right px-3 tabular-nums text-violet-300">{fmt(a.unique_visitors)}</td>
                                <td className="text-right px-3 tabular-nums text-amber-300">{fmt(a.clicks)}</td>
                                <td className="text-right pl-3 tabular-nums font-semibold text-green-400">{money(a.est_value)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default AdsAnalyticsPage;
